/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../../../api/axios";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

export default function PayrollApproval() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState("");

  const normalizeRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.data)) return data.data.data;
    return [];
  };

  const loadRuns = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const res = await axios.get("/payroll-run");
      setRuns(normalizeRows(res?.data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load payroll runs. Please try again.";

      setError(msg);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const canFinalize = useCallback(async (runId) => {
    const res = await axios.get(`/payroll-run/${runId}/exit-due-status`);
    return !res?.data?.blocked;
  }, []);

  const finalizeRun = useCallback(
    async (runId) => {
      if (!runId) return;

      setActionLoadingId(runId);
      setError("");

      try {
        const ok = await canFinalize(runId);
        if (!ok) {
          alert(
            "Payroll finalization blocked: unresolved Exit Due Review records exist.\n\nGo to Payroll > Exit Due Review and resolve all pending exits."
          );
          return;
        }

        await axios.post(`/payroll-run/${runId}/finalize`);
        alert("Payroll finalized successfully.");
        await loadRuns();
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Finalize failed.";

        setError(msg);
        alert(msg);
      } finally {
        setActionLoadingId(null);
      }
    },
    [canFinalize, loadRuns]
  );

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Month", accessor: "month" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Action",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const runId = row?.original?.id;
          const busy = actionLoadingId === runId;

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                size="small"
                color="success"
                disabled={!runId || busy}
                onClick={() => finalizeRun(runId)}
              >
                {busy ? "Working..." : "Finalize"}
              </MDButton>
            </MDBox>
          );
        },
      },
    ],
    [actionLoadingId, finalizeRun]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDTypography variant="h5">Payroll Approval</MDTypography>
        <MDTypography variant="button" color="text">
          Multi-level authorization. Finalize will be blocked if Exit Due Review is unresolved.
        </MDTypography>

        {error ? (
          <MDBox mt={2} p={2} borderRadius={2} sx={{ border: "1px solid #f5c2c7" }}>
            <MDTypography variant="button" color="error">
              {error}
            </MDTypography>
          </MDBox>
        ) : null}

        <MDBox mt={3}>
          <MDButton color="info" onClick={loadRuns} disabled={loading}>
            {loading ? "Loading..." : "Refresh Runs"}
          </MDButton>
        </MDBox>

        <MDBox mt={3}>
          <DataTable table={{ columns, rows: runs }} entriesPerPage showTotalEntries noEndBorder />
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
